// Grave Armor (Tough(+3)) -> Upgrade granting Rule
// Ancient Banner (Fear) -> Upgrade granting Rule
// Precision Shots -> Rule
// Combat Bike (Fast, Impact(1), Twin Rifle (30”,A4)) -> Upgrade granting Rule and Weapon
// Shred Pistol (6”, A2, Rending) -> Weapon
// Energy Sword (A2, AP(1), Rending), Energy Fist (A2, AP(3)), Fist-Pistol (12”, A3) -> [Items,Rule]
import { GainType, UnitProductInfo } from "@/data/types";
import { orderBy, sortBy } from "lodash-es";
import ArmyBookRule from "./ArmyBookRule";
import ArmyBookWeapon from "./ArmyBookWeapon";

/**
 * a combined ite, be it a renamed special rule, a vehicle, bike or mount
 */
export default class ArmyBookItem {
  public id: string;
  public type = GainType.Item;
  public name = "";
  public content: (ArmyBookWeapon | ArmyBookRule)[] = [];
  public bases: { round: string; square: string } = null;
  public product: UnitProductInfo = undefined;

  /**
   *
   * @param name {string}
   * @param content {[ArmyBookRule|ArmyBookWeapon]}
   */
  constructor(
    name,
    content = [],
    bases: { round: string; square: string } = null,
    id: string = undefined,
    product?: UnitProductInfo
  ) {
    this.name = name;
    this.content = ArmyBookItem.sortContent(content);
    this.bases = bases;
    this.product = product;
    if (id) this.id = id;
  }

  static sortContent(content: any[]) {
    return orderBy(content, [
      // First sort by type: ArmyBookWeapon (0) first, then ArmyBookRule (1)
      (item) => {
        if (item instanceof ArmyBookWeapon) return 0; // ArmyBookWeapon comes first
        if (item instanceof ArmyBookRule) return 1; // ArmyBookRule comes after
      },
      // Then sort ArmyBookWeapon by weapon.range and ArmyBookRule by rule.name
      (item) => {
        if (item instanceof ArmyBookWeapon) return -item.range; // Sort weapons by range
        if (item instanceof ArmyBookRule) return item.name; // Sort rules by name
      },
      // Then sort ArmyBookWeapon by weapon.range and ArmyBookRule by rule.name
      (item) => {
        if (item instanceof ArmyBookWeapon) return item.name; // Sort weapons by range
      }
    ]);
  }

  /**
   * create the readable representation (label) for this item:
   * <name> (all the things)
   *
   * Sorting isweapons by range, then special rules by alpha
   */
  toString() {
    const content = sortBy(this.content, [
      // First sort by type: ArmyBookWeapon (0) first, then ArmyBookRule (1)
      (item) => {
        if (item instanceof ArmyBookWeapon) return 0; // ArmyBookWeapon comes first
        if (item instanceof ArmyBookRule) return 1; // ArmyBookRule comes after
      },
      // Then sort ArmyBookWeapon by weapon.range and ArmyBookRule by rule.name
      (item) => {
        if (item instanceof ArmyBookWeapon) return -item.range; // Sort weapons by range
        if (item instanceof ArmyBookRule) return item.name; // Sort rules by name
      },
      // Then sort ArmyBookWeapon by weapon.range and ArmyBookRule by rule.name
      (item) => {
        if (item instanceof ArmyBookWeapon) return item.name; // Sort weapons by range
      }
    ]);

    const contentLabels = content.map((item) => item.toString());

    return `${this.name} (${contentLabels.join(", ")})`;
  }

  static FromObject(json) {
    const content = json.content.map((o) => {
      if (o.attacks) {
        return ArmyBookWeapon.FromObject(o);
      } else {
        return ArmyBookRule.FromObject(o);
      }
    });
    return new ArmyBookItem(json.name, content, json.bases, json.id, json.product);
  }

  static Is(string) {
    // e.g. Automa Horse - Fast, Impact(1)
    const matchAoFMount = /^(?<name>[\w -]+) - (?<items>[\w\d(), ”"+]*)$/;
    const mountMatch = matchAoFMount.exec(string);

    // e.g. Combat Bike (Fast, Impact(1), Twin Assault Rifle (24”,A2))
    // e.g. Destroyer Armor (Ambush, Tough(+3))
    const matchGfUpgrade = /^(?<name>[\w -]+) \((?<items>[\w\d(), ”"+]*)\)$/;
    const matchGf = matchGfUpgrade.exec(string);

    return !!mountMatch || !!matchGf;
  }

  static FromSimpleString(string) {
    const match = /^(?<name>[\w\d\s-]+) \((?<items>[\w\d\s(),+]*)\)$/.exec(string);
    if (match) {
      const { name, items } = match.groups;
      const parts = items.split(";").map((item) => item.trim());
      const content = [];
      parts.forEach((part) => {
        if (ArmyBookRule.Is(part)) {
          content.push(ArmyBookRule.FromString(part));
        } else {
          console.warn(`ArmyBookItem: Could not parse`, part);
        }
      });
      return new ArmyBookItem(name, content);
    }
    return false;
  }

  static FromStringSim(itemString) {
    const match = /^(?<name>[\w -]+) [([](?<items>[\w\d(), ”"+]*)[)\]]$/g.exec(itemString);
    if (match) {
      const { name, items } = match.groups;
      const parts = items.split(";").map((item) => item.trim());
      const content = [];
      parts.forEach((part) => {
        if (ArmyBookWeapon.Is(part)) {
          content.push(ArmyBookWeapon.Parse(part));
        } else if (ArmyBookRule.Is(part)) {
          content.push(ArmyBookRule.Parse(part));
        } else {
          console.warn(`ArmyBookItem: Could not parse`, part);
        }
      });
      return new ArmyBookItem(name, content);
    }
  }

  static FromString(itemString) {
    const match = /^(?<name>[\w -]+) [([](?<items>[\w\d(), ”"+]*)[)\]]$/g.exec(itemString);
    if (match) {
      const { name, items } = match.groups;
      // content = Fast, Impact(1), Twin Rifle (30”,A4);
      const matches = items.matchAll(/(, )?(?<item>[\w\d -]+ ?\([\w\d”,+()]+\)|[\w\d -]+)/g);
      let parts = [];
      for (const match of matches) {
        parts.push(match[1]);
      }
      let content = [];
      parts.forEach((part) => {
        if (ArmyBookWeapon.Is(part)) {
          content.push(ArmyBookWeapon.Parse(part));
        } else if (ArmyBookRule.Is(part)) {
          content.push(ArmyBookRule.Parse(part));
        } else {
          console.warn(`ArmyBookItem: Could not parse`, part);
        }
      });
      return new ArmyBookItem(name, content);
    }
  }
}
